import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import ThemeToggle from '@/components/shared/ThemeToggle';
import NavigationMenu from '@/components/shared/Menu';
import TeamCodesDisplay from '@/components/shared/TeamCodesDisplay';
import { getApiUrl } from '@/config/api';
import { safeJsonParse } from '@/utils/apiUtils';
import { TeamCreationRegistrationResponse } from '@/types';

/**
 * User role types for registration
 */
type UserRole = 'athlete' | 'coach' | 'admin';

/**
 * Registration mode - determines which flow user takes
 */
type RegistrationMode = 'join' | 'create' | null;

/**
 * Format up to 10 digits as (xxx) xxx-xxxx as the user types.
 * Strips all non-digit characters and applies US phone formatting.
 * If the user pastes a number with a leading country code (+1 or 1),
 * the prefix is removed so the remaining 10 digits are kept intact
 * rather than silently truncating and storing the wrong number.
 */
function formatPhone(value: string): string {
    let digits = value.replace(/\D/g, '');

    // Strip leading US country code so pasted "+15551234567" → "5551234567"
    if (digits.length === 11 && digits.startsWith('1')) {
        digits = digits.slice(1);
    }

    // Cap at 10 digits — any excess is discarded from the right
    digits = digits.slice(0, 10);

    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Returns true if the string contains exactly 10 digits (valid US phone).
 */
function isPhoneValid(value: string): boolean {
    return value.replace(/\D/g, '').length === 10;
}

/**
 * Theme-aware registration component for CGM Sports AI Dashboard.
 * Features minimalist design with team branding and theme support.
 * Supports both joining existing teams and creating new teams.
 */
const Register: React.FC = () => {
    // Registration mode state
    const [mode, setMode] = useState<RegistrationMode>(null);
    const [teamCodes, setTeamCodes] = useState<{coach_code: string, athlete_code: string} | null>(null);

    // Form data state with both join and create fields
    const [formData, setFormData] = useState({
        // Common fields
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        phone: '',
        smsOptIn: false,
        sex: '' as '' | 'male' | 'female',
        role: 'athlete' as UserRole,

        // Join existing team
        teamCode: '',

        // Create new team
        organizationName: '',
        teamName: '',
        sport: '',
        gender: 'mixed' as 'male' | 'female' | 'mixed',
        organizationDescription: '',
        teamDescription: '',
    });

    const [invitePrefilled, setInvitePrefilled] = useState(false);
    const [codeStatus, setCodeStatus] = useState<'idle' | 'loading' | 'valid' | 'invalid'>('idle');
    const [resolvedRole, setResolvedRole] = useState<UserRole | null>(null);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [userInfo, setUserInfo] = useState<{ username: string; role?: string; full_name?: string; first_name?: string } | null>(null);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { isDark } = useTheme();

    /**
     * Get user info from localStorage on component mount
     */
    React.useEffect(() => {
        const userInfoString = localStorage.getItem('user_info');
        if (userInfoString) {
            try {
                const user = JSON.parse(userInfoString);
                setUserInfo(user);
            } catch (err) {
                console.error('Error parsing user info:', err);
            }
        }
    }, []);

    /**
     * Decode invite token from email link (e.g. /register?invite=TOKEN)
     */
    React.useEffect(() => {
        const inviteToken = searchParams.get('invite');
        if (!inviteToken) return;

        (async () => {
            try {
                const res = await fetch(getApiUrl(`/api/validate-invite-token/?token=${encodeURIComponent(inviteToken)}`));
                const data = await res.json();
                if (data.valid) {
                    setMode('join');
                    setInvitePrefilled(true);
                    setFormData(prev => ({
                        ...prev,
                        email: data.email || '',
                        role: data.role || 'athlete',
                        teamCode: data.team_code || '',
                    }));
                    if (data.role) setResolvedRole(data.role as UserRole);
                    if (data.team_code) setCodeStatus('valid');
                } else {
                    setError(data.error || 'Invalid or expired invitation link');
                }
            } catch {
                setError('Failed to validate invitation link');
            }
        })();
    }, [searchParams]);

    /**
     * Handle input changes with proper typing
     */
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;

        if (name === 'phone') {
            const formatted = formatPhone(value);
            setFormData(prev => ({
                ...prev,
                phone: formatted,
                // Clear opt-in when phone is removed entirely
                smsOptIn: formatted ? prev.smsOptIn : false,
            }));
            return;
        }

        if (name === 'smsOptIn' && type === 'checkbox') {
            setFormData(prev => ({
                ...prev,
                smsOptIn: (e.target as HTMLInputElement).checked,
            }));
            return;
        }

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Team code validation for join mode
        if (name === 'teamCode' && mode === 'join') {
            const code = value.trim();
            setResolvedRole(null);
            if (!code) {
                setCodeStatus('idle');
                return;
            }
            setCodeStatus('loading');
            (async () => {
                try {
                    const res = await fetch(getApiUrl(`/api/validate-team-code/?code=${encodeURIComponent(code)}`));
                    const result = await safeJsonParse<{ valid: boolean; role: UserRole }>(res, 'validating team code');

                    if (result.success && result.data!.valid && (result.data!.role === 'coach' || result.data!.role === 'athlete')) {
                        setResolvedRole(result.data!.role);
                        setCodeStatus('valid');
                    } else {
                        setResolvedRole(null);
                        setCodeStatus('invalid');
                    }
                } catch {
                    setResolvedRole(null);
                    setCodeStatus('invalid');
                }
            })();
        }
    };

    /**
     * Validate form data before submission
     */
    const validateForm = (): boolean => {
        // Common validations
        if (!formData.email.trim()) {
            setError('Email is required');
            return false;
        }
        if (!formData.email.includes('@')) {
            setError('Please enter a valid email address');
            return false;
        }
        if (!formData.firstName.trim()) {
            setError('First name is required');
            return false;
        }
        if (!formData.lastName.trim()) {
            setError('Last name is required');
            return false;
        }
        if (formData.password.length < 6) {
            setError('Password must be at least 6 characters long');
            return false;
        }
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return false;
        }

        // Phone validation (optional, but must be valid if provided)
        if (formData.phone && !isPhoneValid(formData.phone)) {
            setError('Please enter a valid 10-digit phone number');
            return false;
        }

        // Create mode specific validations
        if (mode === 'create') {
            if (!formData.organizationName.trim()) {
                setError('Organization name is required');
                return false;
            }
            // Sport is optional to support gym groups and other non-sport organizations
        }

        return true;
    };

    /**
     * Handle form submission with proper error handling
     */
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);

        try {
            // Determine endpoint and payload based on mode
            const endpoint = mode === 'create' ? '/api/register/v2/' : '/api/register/';

            // Strip formatting from phone -- send digits only
            const phoneDigits = formData.phone.replace(/\D/g, '') || undefined;

            const payload = mode === 'create' ? {
                username: formData.email,
                email: formData.email,
                password: formData.password,
                first_name: formData.firstName,
                last_name: formData.lastName,
                phone: phoneDigits,
                sms_opt_in: formData.smsOptIn,
                role: 'coach',
                sex: formData.sex || undefined,
                create_team: true,
                organization_name: formData.organizationName,
                team_name: formData.teamName || undefined,
                sport: formData.sport,
                gender: formData.gender,
                organization_description: formData.organizationDescription || undefined,
                team_description: formData.teamDescription || undefined,
            } : {
                username: formData.email,
                email: formData.email,
                password: formData.password,
                first_name: formData.firstName,
                last_name: formData.lastName,
                phone: phoneDigits,
                sms_opt_in: formData.smsOptIn,
                role: (resolvedRole ?? formData.role),
                sex: formData.sex || undefined,
                team_code: formData.teamCode,
            };

            const response = await fetch(getApiUrl(endpoint), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            const result = await safeJsonParse<TeamCreationRegistrationResponse>(response, 'user registration');

            if (!result.success) {
                if (result.error) {
                    throw new Error(result.error);
                } else {
                    throw new Error('Registration failed');
                }
            }

            // Handle success based on mode
            if (mode === 'create' && result.data?.team_created && result.data.team_codes) {
                // Show team codes for create mode
                setTeamCodes(result.data.team_codes);
                setSuccess('Registration successful!');
            } else {
                // Join mode - show success and redirect
                setSuccess('Registration successful! You can now log in.');

                // Reset form
                setFormData({
                    email: '',
                    password: '',
                    confirmPassword: '',
                    firstName: '',
                    lastName: '',
                    phone: '',
                    smsOptIn: false,
                    sex: '',
                    role: 'athlete',
                    teamCode: '',
                    organizationName: '',
                    teamName: '',
                    sport: '',
                    gender: 'mixed',
                    organizationDescription: '',
                    teamDescription: '',
                });

                // Redirect to login after 3 seconds
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            }

        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred during registration');
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Handle mode selection
     */
    const selectMode = (selectedMode: RegistrationMode) => {
        setMode(selectedMode);
        setError('');
        setSuccess('');
        // Set role to coach for create mode
        if (selectedMode === 'create') {
            setFormData(prev => ({ ...prev, role: 'coach' }));
        }
    };

    /**
     * Go back to mode selection
     */
    const goBack = () => {
        setMode(null);
        setTeamCodes(null);
        setError('');
        setSuccess('');
    };

    // Show team codes after successful team creation
    if (teamCodes) {
        return (
            <div className="min-h-screen cgm-bg-primary flex flex-col px-4 sm:px-6 lg:px-8 transition-colors duration-300">
                <div className="flex-1 min-h-[2rem]"></div>

                {/* Navigation Menu */}
                <div className="fixed top-4 left-4 z-50">
                    <NavigationMenu
                        userInfo={userInfo}
                        isDark={isDark}
                        showNavigation={true}
                        className="my-auto"
                    />
                </div>

                {/* Theme Toggle */}
                <div className="fixed top-4 right-4 z-50">
                    <ThemeToggle />
                </div>

                <div className="flex-1 flex items-center justify-center py-8">
                    <div className="max-w-md w-full">
                        <TeamCodesDisplay
                            coachCode={teamCodes.coach_code}
                            athleteCode={teamCodes.athlete_code}
                            teamName={formData.teamName || `${formData.organizationName} ${formData.sport} Team`}
                            organizationName={formData.organizationName}
                        />
                    </div>
                </div>

                <div className="flex-1 min-h-[2rem]"></div>
            </div>
        );
    }

    // Show mode selection if no mode selected
    if (!mode) {
        return (
            <div className="min-h-screen cgm-bg-primary flex flex-col px-4 sm:px-6 lg:px-8 transition-colors duration-300">
                <div className="flex-1 min-h-[2rem]"></div>

                {/* Navigation Menu */}
                <div className="fixed top-4 left-4 z-50">
                    <NavigationMenu
                        userInfo={userInfo}
                        isDark={isDark}
                        showNavigation={true}
                        className="my-auto"
                    />
                </div>

                {/* Theme Toggle */}
                <div className="fixed top-4 right-4 z-50">
                    <ThemeToggle />
                </div>

                {/* Mode Selection */}
                <div className="flex-1 flex items-center justify-center py-8">
                    <div className="max-w-4xl w-full space-y-6">
                        {/* Header */}
                        <div className="text-center">
                            <img
                                className="mx-auto h-20 w-auto mb-6 transition-all duration-200 hover:scale-110 cursor-pointer"
                                src={isDark ? "/logos/LOGO-MAIN-WHITE.svg" : "/logos/LOGO-MAIN-BLK.svg"}
                                alt="Team Logo"
                                onClick={() => navigate('/')}
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = isDark
                                        ? "/logos/LOGO-MAIN-WHITE.png"
                                        : "/logos/LOGO-MAIN-BLK.png";
                                }}
                            />
                            <h2 className="text-3xl font-light tracking-tight cgm-text-primary mb-2">
                                Get Started
                            </h2>
                            <p className="cgm-text-secondary text-base">
                                Choose how you'd like to join CGM Sports AI Dashboard
                            </p>
                        </div>

                        {/* Mode Selection Cards */}
                        <div className="grid md:grid-cols-2 gap-6">
                            {/* Create New Team Card */}
                            <button
                                onClick={() => selectMode('create')}
                                className="cgm-card p-8 text-left hover:scale-105 transition-all duration-200 hover:shadow-xl cursor-pointer group"
                            >
                                <div className="flex flex-col h-full">
                                    <div className="mb-4">
                                        <div className="w-12 h-12 rounded-lg cgm-list-item flex items-center justify-center mb-4">
                                            <svg className="w-6 h-6 cgm-icon-data" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                            </svg>
                                        </div>
                                        <h3 className="text-xl font-semibold cgm-text-primary mb-2">
                                            Create New Team
                                        </h3>
                                        <p className="cgm-text-secondary text-sm">
                                            Start fresh by creating your own organization and team. Perfect for coaches setting up a new program.
                                        </p>
                                    </div>
                                    <div className="mt-auto pt-4">
                                        <div className="flex items-center cgm-text-primary group-hover:translate-x-2 transition-transform duration-200">
                                            <span className="text-sm font-medium">Get started</span>
                                            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </button>

                            {/* Join Existing Team Card */}
                            <button
                                onClick={() => selectMode('join')}
                                className="cgm-card p-8 text-left hover:scale-105 transition-all duration-200 hover:shadow-xl cursor-pointer group"
                            >
                                <div className="flex flex-col h-full">
                                    <div className="mb-4">
                                        <div className="w-12 h-12 rounded-lg cgm-list-item flex items-center justify-center mb-4">
                                            <svg className="w-6 h-6 cgm-icon-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                            </svg>
                                        </div>
                                        <h3 className="text-xl font-semibold cgm-text-primary mb-2">
                                            Join Existing Team
                                        </h3>
                                        <p className="cgm-text-secondary text-sm">
                                            Have an invite code? Join an existing team as a coach or athlete.
                                        </p>
                                    </div>
                                    <div className="mt-auto pt-4">
                                        <div className="flex items-center cgm-text-primary group-hover:translate-x-2 transition-transform duration-200">
                                            <span className="text-sm font-medium">Join now</span>
                                            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </button>
                        </div>

                        {/* Already have account link */}
                        <div className="text-center pt-4">
                            <p className="cgm-text-muted text-sm">
                                Already have an account?{' '}
                                <Link
                                    to="/login"
                                    className="cgm-text-primary hover:cgm-text-secondary font-medium transition-colors duration-200 underline underline-offset-4"
                                >
                                    Sign in here
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex-1 min-h-[2rem]"></div>
            </div>
        );
    }

    // Show registration form (join or create mode)
    return (
        <div className="min-h-screen cgm-bg-primary flex flex-col px-4 sm:px-6 lg:px-8 transition-colors duration-300">
            <div className="flex-1 min-h-[2rem]"></div>

            {/* Navigation Menu */}
            <div className="fixed top-4 left-4 z-50">
                <NavigationMenu
                    userInfo={userInfo}
                    isDark={isDark}
                    showNavigation={true}
                    className="my-auto"
                />
            </div>

            {/* Theme Toggle */}
            <div className="fixed top-4 right-4 z-50">
                <ThemeToggle />
            </div>

            {/* Registration Form */}
            <div className="flex-1 flex items-center justify-center py-8">
                <div className="max-w-md w-full space-y-6">
                    {/* Header */}
                    <div className="text-center">
                        <img
                            className="mx-auto h-20 w-auto mb-6 transition-all duration-200 hover:scale-110 cursor-pointer"
                            src={isDark ? "/logos/LOGO-MAIN-WHITE.svg" : "/logos/LOGO-MAIN-BLK.svg"}
                            alt="Team Logo"
                            onClick={() => navigate('/')}
                            onError={(e) => {
                                (e.target as HTMLImageElement).src = isDark
                                    ? "/logos/LOGO-MAIN-WHITE.png"
                                    : "/logos/LOGO-MAIN-BLK.png";
                            }}
                        />
                        <h2 className="text-2xl font-light tracking-tight cgm-text-primary mb-2">
                            {mode === 'create' ? 'Create Your Team' : 'Join a Team'}
                        </h2>
                        <p className="cgm-text-secondary text-sm">
                            {mode === 'create'
                                ? 'Set up your organization and team'
                                : 'Enter your details to join'
                            }
                        </p>
                    </div>

                    {/* Form Card */}
                    <div className="cgm-card p-6">
                        {/* Back Button */}
                        <button
                            onClick={goBack}
                            className="cgm-text-secondary hover:cgm-text-primary mb-4 flex items-center text-sm transition-colors duration-200"
                        >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                            Back to options
                        </button>

                        {error && (
                            <div className="cgm-alert-error px-4 py-3 rounded-md mb-4 text-sm">
                                {error}
                            </div>
                        )}

                        {success && (
                            <div className="cgm-alert-success px-4 py-3 rounded-md mb-4 text-sm">
                                {success}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-4">
                            {/* User Information Section */}
                            <div className="space-y-4">
                                <h3 className="text-sm font-semibold cgm-text-primary">Personal Information</h3>

                                {/* Name Fields */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label htmlFor="firstName" className="block text-sm font-medium cgm-text-secondary mb-1">
                                            First Name
                                        </label>
                                        <input
                                            id="firstName"
                                            name="firstName"
                                            type="text"
                                            required
                                            value={formData.firstName}
                                            onChange={handleInputChange}
                                            disabled={isLoading}
                                            className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                            placeholder="First name"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="lastName" className="block text-sm font-medium cgm-text-secondary mb-1">
                                            Last Name
                                        </label>
                                        <input
                                            id="lastName"
                                            name="lastName"
                                            type="text"
                                            required
                                            value={formData.lastName}
                                            onChange={handleInputChange}
                                            disabled={isLoading}
                                            className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                            placeholder="Last name"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium cgm-text-secondary mb-1">
                                        Email Address
                                    </label>
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        required
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        disabled={isLoading || invitePrefilled}
                                        readOnly={invitePrefilled}
                                        className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        placeholder="Enter your email"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="phone" className="block text-sm font-medium cgm-text-secondary mb-1">
                                        Phone Number <span className="cgm-text-muted text-xs font-normal">(optional)</span>
                                    </label>
                                    <input
                                        id="phone"
                                        name="phone"
                                        type="tel"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        disabled={isLoading}
                                        className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        placeholder="(555) 123-4567"
                                    />
                                </div>

                                {/* SMS opt-in consent -- only visible when a phone number is entered */}
                                {formData.phone && (
                                    <label htmlFor="smsOptIn" className="flex items-start gap-2 cursor-pointer select-none">
                                        <input
                                            id="smsOptIn"
                                            name="smsOptIn"
                                            type="checkbox"
                                            checked={formData.smsOptIn}
                                            onChange={handleInputChange}
                                            disabled={isLoading}
                                            className="mt-0.5 h-4 w-4 rounded border-gray-300 accent-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                        />
                                        <span className="text-xs cgm-text-muted leading-relaxed">
                                            By checking this box, I consent to receive recurring SMS messages from CGM Sports
                                            related to my account, performance program, training reminders, readiness summaries,
                                            session check-in prompts, and operational updates from my coach or program administrator.
                                            Message frequency varies. Message and data rates may apply. Reply HELP for help, STOP
                                            to cancel. Consent is not a condition of any purchase. Mobile information and messaging
                                            consent are not shared with third parties or affiliates for marketing or promotional
                                            purposes. See our{' '}
                                            <Link
                                                to="/privacy"
                                                target="_blank"
                                                rel="noopener"
                                                onClick={(e) => e.stopPropagation()}
                                                className="underline hover:no-underline"
                                            >
                                                Privacy Policy
                                            </Link>{' '}
                                            and{' '}
                                            <Link
                                                to="/terms"
                                                target="_blank"
                                                rel="noopener"
                                                onClick={(e) => e.stopPropagation()}
                                                className="underline hover:no-underline"
                                            >
                                                Terms &amp; Conditions
                                            </Link>.
                                        </span>
                                    </label>
                                )}

                                <div>
                                    <label htmlFor="sex" className="block text-sm font-medium cgm-text-secondary mb-1">
                                        Sex <span className="cgm-text-muted text-xs font-normal">(optional)</span>
                                    </label>
                                    <select
                                        id="sex"
                                        name="sex"
                                        value={formData.sex}
                                        onChange={handleInputChange}
                                        disabled={isLoading}
                                        className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <option value="">Select</option>
                                        <option value="male">Male</option>
                                        <option value="female">Female</option>
                                    </select>
                                </div>

                                <div>
                                    <label htmlFor="password" className="block text-sm font-medium cgm-text-secondary mb-1">
                                        Password
                                    </label>
                                    <input
                                        id="password"
                                        name="password"
                                        type="password"
                                        required
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        disabled={isLoading}
                                        className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        placeholder="Create a password"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="confirmPassword" className="block text-sm font-medium cgm-text-secondary mb-1">
                                        Confirm Password
                                    </label>
                                    <input
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        type="password"
                                        required
                                        value={formData.confirmPassword}
                                        onChange={handleInputChange}
                                        disabled={isLoading}
                                        className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        placeholder="Re-enter your password"
                                    />
                                </div>
                            </div>

                            {/* Join Mode Fields */}
                            {mode === 'join' && (
                                <>
                                    <div className="border-t cgm-border-primary pt-4">
                                        <h3 className="text-sm font-semibold cgm-text-primary mb-4">Team Information</h3>

                                        <div>
                                            <label htmlFor="role" className="block text-sm font-medium cgm-text-secondary mb-1">
                                                Role
                                            </label>
                                            <select
                                                id="role"
                                                name="role"
                                                required
                                                value={resolvedRole ?? formData.role}
                                                onChange={handleInputChange}
                                                disabled={isLoading || codeStatus === 'valid'}
                                                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <option value="athlete">Athlete</option>
                                                <option value="coach">Coach</option>
                                            </select>
                                        </div>

                                        <div className="relative mt-4">
                                            <label htmlFor="teamCode" className="block text-sm font-medium cgm-text-secondary mb-1">
                                                Team Join Code
                                            </label>
                                            <input
                                                id="teamCode"
                                                name="teamCode"
                                                type="text"
                                                value={formData.teamCode}
                                                onChange={handleInputChange}
                                                disabled={isLoading}
                                                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                placeholder="Enter coach or athlete join code"
                                            />
                                            <div className="absolute right-3 top-11.5 -translate-y-1/2 flex items-center pointer-events-none">
                                                {codeStatus === 'loading' && (
                                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-gray-500"></div>
                                                )}
                                                {codeStatus === 'valid' && (
                                                    <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                )}
                                                {codeStatus === 'invalid' && (
                                                    <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Create Mode Fields */}
                            {mode === 'create' && (
                                <>
                                    <div className="border-t cgm-border-primary pt-4 space-y-4">
                                        <h3 className="text-sm font-semibold cgm-text-primary">Organization Details</h3>

                                        <div>
                                            <label htmlFor="organizationName" className="block text-sm font-medium cgm-text-secondary mb-1">
                                                Organization Name <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                id="organizationName"
                                                name="organizationName"
                                                type="text"
                                                required
                                                value={formData.organizationName}
                                                onChange={handleInputChange}
                                                disabled={isLoading}
                                                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                placeholder="Enter your organization name"
                                            />
                                        </div>

                                        <div>
                                            <label htmlFor="organizationDescription" className="block text-sm font-medium cgm-text-secondary mb-1">
                                                Description (optional)
                                            </label>
                                            <textarea
                                                id="organizationDescription"
                                                name="organizationDescription"
                                                rows={2}
                                                value={formData.organizationDescription}
                                                onChange={handleInputChange}
                                                disabled={isLoading}
                                                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                placeholder="Brief description of your organization"
                                            />
                                        </div>
                                    </div>

                                    <div className="border-t cgm-border-primary pt-4 space-y-4">
                                        <h3 className="text-sm font-semibold cgm-text-primary">Team Details</h3>

                                        <div>
                                            <label htmlFor="sport" className="block text-sm font-medium cgm-text-secondary mb-1">
                                                Sport or Activity (optional)
                                            </label>
                                            <input
                                                id="sport"
                                                name="sport"
                                                type="text"
                                                value={formData.sport}
                                                onChange={handleInputChange}
                                                disabled={isLoading}
                                                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                placeholder="Wrestling, Crossfit, General Fitness, etc."
                                            />
                                        </div>

                                        <div>
                                            <label htmlFor="gender" className="block text-sm font-medium cgm-text-secondary mb-1">
                                                Gender Category
                                            </label>
                                            <select
                                                id="gender"
                                                name="gender"
                                                value={formData.gender}
                                                onChange={handleInputChange}
                                                disabled={isLoading}
                                                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <option value="mixed">Mixed</option>
                                                <option value="male">Men's</option>
                                                <option value="female">Women's</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label htmlFor="teamName" className="block text-sm font-medium cgm-text-secondary mb-1">
                                                Team Name (optional)
                                            </label>
                                            <input
                                                id="teamName"
                                                name="teamName"
                                                type="text"
                                                value={formData.teamName}
                                                onChange={handleInputChange}
                                                disabled={isLoading}
                                                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                placeholder="Leave blank to auto-generate"
                                            />
                                            <p className="text-xs cgm-text-muted mt-1">
                                                If blank, will be generated as: {formData.organizationName || 'Organization'} {formData.sport} {formData.gender !== 'mixed' ? (formData.gender === 'male' ? "(Men's)" : "(Women's)") : ''} Team
                                            </p>
                                        </div>

                                        <div>
                                            <label htmlFor="teamDescription" className="block text-sm font-medium cgm-text-secondary mb-1">
                                                Team Description (optional)
                                            </label>
                                            <textarea
                                                id="teamDescription"
                                                name="teamDescription"
                                                rows={2}
                                                value={formData.teamDescription}
                                                onChange={handleInputChange}
                                                disabled={isLoading}
                                                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                placeholder="Additional details about your team"
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            <p className="text-xs cgm-text-muted text-center leading-relaxed mt-6">
                                By creating an account, you agree to our{' '}
                                <Link
                                    to="/terms"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="cgm-text-primary underline underline-offset-2 hover:cgm-text-secondary transition-colors"
                                >
                                    Terms of Service
                                </Link>
                                {' '}and{' '}
                                <Link
                                    to="/privacy"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="cgm-text-primary underline underline-offset-2 hover:cgm-text-secondary transition-colors"
                                >
                                    Privacy Policy
                                </Link>
                                .
                            </p>

                            <button
                                type="submit"
                                disabled={isLoading || (mode === 'join' && !!formData.teamCode && codeStatus !== 'valid')}
                                className="cgm-button w-full mt-3 py-3 px-4 flex items-center justify-center"
                            >
                                {isLoading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-5 w-5 mr-3 cgm-spinner border-2"></div>
                                        {mode === 'create' ? 'Creating Team...' : 'Creating Account...'}
                                    </>
                                ) : (
                                    mode === 'create' ? 'Create Team & Account' : 'Create Account'
                                )}
                            </button>
                        </form>

                        {/* Navigation Links */}
                        <div className="mt-6 space-y-4 text-center">
                            <p className="cgm-text-muted text-sm">
                                Already have an account?{' '}
                                <Link
                                    to="/login"
                                    className="cgm-text-primary hover:cgm-text-secondary font-medium transition-colors duration-200 underline underline-offset-4"
                                >
                                    Sign in here
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex-1 min-h-[2rem]"></div>
        </div>
    );
};

export default Register;
